import React from 'react';
import { supabase } from '../../lib/supabase';
import { ROXO, TEXTO, SUAVE, BORDA, AMBAR, VERMELHO, fmtMoeda, fmtData, botaoPrimario, botaoSecundario, botaoPequeno, th, td, desabilitado } from './comum';

// Notas fiscais de serviço emitidas → Clientes a Receber × Receita.
// O PDF/XML é lido por IA no servidor (api/nota-fiscal.js); a tela mostra os
// dados para conferência e só grava em contabil_lancamentos ao confirmar.
// chave_dedup 'nf:<cnpj do prestador>:<número>' impede lançar a mesma nota duas vezes.
// O arquivo não é guardado: só os dados da nota viram lançamento.

const dig = (s) => String(s || '').replace(/\D/g, '');

function lerComo(file, modo) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(modo === 'base64' ? String(r.result).split(',')[1] || '' : String(r.result));
    r.onerror = () => reject(r.error);
    if (modo === 'base64') r.readAsDataURL(file); else r.readAsText(file);
  });
}

export default function NotasFiscaisPainel({
  empresaSlug, empresaCnpj, periodo, nomeMes, inicioLancamentos, periodosFechados, contas, lancamentosContabeis,
  onLancado, setAviso, cardStyle, inputStyle,
}) {
  const [rascunhos, setRascunhos] = React.useState([]);
  const [gravando, setGravando] = React.useState(false);
  const seq = React.useRef(0);

  const contaClientes = React.useMemo(() => (contas.find(c => /clientes a receber/i.test(c.nome)) || {}).rotulo || '', [contas]);
  const contaReceita = React.useMemo(() => (contas.find(c => c.codigo.startsWith('4.1')) || {}).rotulo || '', [contas]);

  React.useEffect(() => { setRascunhos([]); }, [empresaSlug]);

  const lancadasNoMes = React.useMemo(
    () => (lancamentosContabeis || []).filter(l => l.origem === 'nota-fiscal' && (l.data || '').slice(0, 7) === periodo),
    [lancamentosContabeis, periodo],
  );

  const novoRascunho = (extra = {}) => ({
    id: ++seq.current, arquivo: null, lendo: false, erro: null, retencoes: null, prestadorCnpj: null,
    numero: '', data: `${periodo}-01`, cliente: '', descricao: '', valor: '',
    contaDebito: contaClientes, contaCredito: contaReceita, ...extra,
  });
  const atualizar = (id, campos) => setRascunhos(lista => lista.map(r => (r.id === id ? { ...r, ...campos } : r)));

  const aoAnexar = async (e) => {
    const arquivos = Array.from(e.target.files || []);
    e.target.value = '';
    if (!arquivos.length) return;
    setAviso(null);
    const novos = arquivos.map(f => novoRascunho({ arquivo: f.name, lendo: true }));
    setRascunhos(lista => [...lista, ...novos]);
    const { data: sessao } = await supabase.auth.getSession();
    const token = sessao?.session?.access_token || '';
    // Uma de cada vez: várias leituras de IA em paralelo estouram o limite do provedor.
    for (let i = 0; i < arquivos.length; i++) {
      const f = arquivos[i];
      const id = novos[i].id;
      try {
        const ext = (f.name.split('.').pop() || '').toLowerCase();
        if (!['pdf', 'xml'].includes(ext)) throw new Error('Formato não aceito. Envie a nota em PDF ou XML.');
        if (f.size > 3 * 1024 * 1024) throw new Error('O arquivo tem mais de 3 MB.');
        const corpo = ext === 'pdf' ? { pdf: await lerComo(f, 'base64') } : { xml: await lerComo(f, 'texto') };
        const resp = await fetch('/api/nota-fiscal', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify(corpo),
        });
        const json = await resp.json().catch(() => ({}));
        if (!resp.ok || !json.nota) throw new Error(json.error || 'Não foi possível ler a nota.');
        const n = json.nota;
        atualizar(id, {
          lendo: false,
          numero: n.numero || '',
          data: n.data_emissao || `${periodo}-01`,
          cliente: n.tomador_nome || '',
          descricao: n.descricao || '',
          valor: n.valor_servicos ? String(n.valor_servicos) : '',
          retencoes: n.retencoes,
          prestadorCnpj: n.prestador_cnpj,
        });
      } catch (err) {
        console.error('[notas-fiscais] Falha ao ler a nota.', err);
        atualizar(id, { lendo: false, erro: err.message || 'Não foi possível ler a nota.' });
      }
    }
  };

  const alertasDe = (r) => {
    const a = [];
    if (r.prestadorCnpj && dig(empresaCnpj) && r.prestadorCnpj !== dig(empresaCnpj)) a.push({ grave: true, texto: `Prestador da nota (CNPJ ${r.prestadorCnpj}) não é esta empresa.` });
    const totalRet = r.retencoes ? Object.values(r.retencoes).reduce((s, v) => s + (Number(v) || 0), 0) : 0;
    if (totalRet > 0) a.push({ grave: false, texto: `Nota com ${fmtMoeda(totalRet)} de retenções: o lançamento usa o valor bruto; lance as retenções à parte.` });
    if (r.data && inicioLancamentos && r.data < inicioLancamentos) a.push({ grave: true, texto: `Data anterior ao início da escrituração no sistema (${fmtData(inicioLancamentos)}).` });
    if (r.data && periodosFechados.has(r.data.slice(0, 7))) a.push({ grave: true, texto: `O mês ${r.data.slice(5, 7)}/${r.data.slice(0, 4)} está fechado.` });
    if (r.data && r.data.slice(0, 7) !== periodo) a.push({ grave: false, texto: `Nota de outra competência (${r.data.slice(5, 7)}/${r.data.slice(0, 4)}): será lançada nesse mês.` });
    return a;
  };

  const prontos = rascunhos.filter(r => !r.lendo && !r.erro);

  const lancar = async (lista) => {
    const validos = [];
    for (const r of lista) {
      const faltando = [!r.numero.trim() && 'número', !r.data && 'data', !(parseFloat(r.valor) > 0) && 'valor', !r.contaDebito && 'conta débito', !r.contaCredito && 'conta crédito'].filter(Boolean);
      if (faltando.length) { setAviso({ tipo: 'erro', texto: `Nota ${r.numero || r.arquivo || ''}: preencha ${faltando.join(', ')}.` }); return; }
      if (alertasDe(r).some(a => a.grave)) { setAviso({ tipo: 'erro', texto: `Nota ${r.numero}: ${alertasDe(r).find(a => a.grave).texto}` }); return; }
      validos.push(r);
    }
    if (!validos.length) return;
    setGravando(true);
    const prefixo = dig(empresaCnpj) || empresaSlug;
    const registros = validos.map(r => ({
      empresa_slug: empresaSlug,
      data: r.data,
      historico: ['NF ' + r.numero.trim(), r.cliente.trim(), r.descricao.trim()].filter(Boolean).join(' — ').slice(0, 300),
      conta_debito: r.contaDebito,
      conta_credito: r.contaCredito,
      valor: Math.round(parseFloat(r.valor) * 100) / 100,
      documento: `NF ${r.numero.trim()}`,
      origem: 'nota-fiscal',
      chave_dedup: `nf:${prefixo}:${r.numero.trim().replace(/^0+/, '')}`,
    }));
    const { data, error } = await supabase.from('contabil_lancamentos')
      .upsert(registros, { onConflict: 'empresa_slug,chave_dedup', ignoreDuplicates: true })
      .select('chave_dedup');
    setGravando(false);
    if (error) {
      const m = error.message || '';
      if (!/está fechado|anterior ao início da escrituração/.test(m)) console.error('[notas-fiscais] Falha ao lançar.', error);
      setAviso({ tipo: 'erro', texto: /está fechado|anterior ao início da escrituração/.test(m) ? m : 'Não foi possível lançar as notas. Tente novamente.' });
      return;
    }
    const gravadas = new Set((data || []).map(d => d.chave_dedup));
    const repetidas = registros.filter(r => !gravadas.has(r.chave_dedup)).map(r => r.documento);
    const total = registros.filter(r => gravadas.has(r.chave_dedup)).reduce((s, r) => s + r.valor, 0);
    setRascunhos(lista2 => lista2.filter(r => !validos.some(v => v.id === r.id)));
    setAviso({
      tipo: repetidas.length ? 'alerta' : 'sucesso',
      texto: `${gravadas.size} nota(s) lançada(s), total ${fmtMoeda(total)}.${repetidas.length ? ` Já lançadas antes (ignoradas): ${repetidas.join(', ')}.` : ''}`,
    });
    if (onLancado) onLancado();
  };

  const opcoesContas = contas.map(c => <option key={c.codigo} value={c.rotulo}>{c.rotulo}</option>);
  const celula = { ...td, verticalAlign: 'top' };
  const campo = { ...inputStyle, padding: '6px 8px', fontSize: 13, width: '100%', boxSizing: 'border-box' };

  return (
    <div style={cardStyle}>
      <h4 style={{ color: TEXTO, margin: '0 0 4px 0' }}>Notas fiscais emitidas</h4>
      <p style={{ fontSize: 12, color: SUAVE, margin: '0 0 12px 0' }}>
        Anexe as notas em PDF ou XML para leitura automática, ou lance os dados à mão. Cada nota vira um lançamento a débito de Clientes a Receber e a crédito da receita. Nada é gravado antes da sua confirmação.
      </p>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: rascunhos.length ? 12 : 0 }}>
        <label style={{ ...botaoPrimario, display: 'inline-block' }}>
          🧾 Anexar notas (PDF ou XML)
          <input type="file" accept=".pdf,.xml" multiple style={{ display: 'none' }} onChange={aoAnexar} />
        </label>
        <button onClick={() => setRascunhos(l => [...l, novoRascunho()])} style={botaoSecundario}>+ Lançar nota à mão</button>
      </div>

      {rascunhos.length > 0 && (
        <>
          <div style={{ overflowX: 'auto', border: `1px solid ${BORDA}`, borderRadius: 8 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 760 }}>
              <thead>
                <tr>
                  <th style={{ ...th, width: 90 }}>Número</th>
                  <th style={{ ...th, width: 140 }}>Emissão</th>
                  <th style={th}>Cliente e serviço</th>
                  <th style={{ ...th, width: 120 }}>Valor bruto</th>
                  <th style={th}>Contas</th>
                  <th style={{ ...th, width: 90 }}><span style={{ position: 'absolute', left: -9999 }}>Ações</span></th>
                </tr>
              </thead>
              <tbody>
                {rascunhos.map(r => {
                  if (r.lendo) return <tr key={r.id}><td style={celula} colSpan={6}><span style={{ color: SUAVE }}>Lendo {r.arquivo}…</span></td></tr>;
                  if (r.erro) {
                    return (
                      <tr key={r.id}>
                        <td style={{ ...celula, color: VERMELHO }} colSpan={5}>{r.arquivo}: {r.erro}</td>
                        <td style={celula}><button onClick={() => setRascunhos(l => l.filter(x => x.id !== r.id))} style={botaoPequeno}>Remover</button></td>
                      </tr>
                    );
                  }
                  const alertas = alertasDe(r);
                  return (
                    <tr key={r.id}>
                      <td style={celula}><input aria-label="Número da nota" style={campo} value={r.numero} onChange={e => atualizar(r.id, { numero: e.target.value })} /></td>
                      <td style={celula}><input aria-label="Data de emissão" type="date" style={campo} value={r.data} onChange={e => atualizar(r.id, { data: e.target.value })} /></td>
                      <td style={celula}>
                        <input aria-label="Cliente" placeholder="Cliente" style={campo} value={r.cliente} onChange={e => atualizar(r.id, { cliente: e.target.value })} />
                        <input aria-label="Serviço" placeholder="Serviço" style={{ ...campo, marginTop: 4 }} value={r.descricao} onChange={e => atualizar(r.id, { descricao: e.target.value })} />
                        {r.arquivo && <div style={{ fontSize: 11, color: SUAVE, marginTop: 4 }}>{r.arquivo}</div>}
                        {alertas.map((a, k) => <div key={k} style={{ fontSize: 11, color: a.grave ? VERMELHO : AMBAR, marginTop: 4 }}>{a.texto}</div>)}
                      </td>
                      <td style={celula}><input aria-label="Valor bruto" type="number" step="0.01" min="0" style={campo} value={r.valor} onChange={e => atualizar(r.id, { valor: e.target.value })} /></td>
                      <td style={celula}>
                        <select aria-label="Conta débito" style={campo} value={r.contaDebito} onChange={e => atualizar(r.id, { contaDebito: e.target.value })}>
                          <option value="">D — selecione</option>{opcoesContas}
                        </select>
                        <select aria-label="Conta crédito" style={{ ...campo, marginTop: 4 }} value={r.contaCredito} onChange={e => atualizar(r.id, { contaCredito: e.target.value })}>
                          <option value="">C — selecione</option>{opcoesContas}
                        </select>
                      </td>
                      <td style={{ ...celula, whiteSpace: 'nowrap' }}>
                        <button onClick={() => lancar([r])} disabled={gravando} style={desabilitado({ ...botaoPequeno, color: ROXO, border: `1px solid ${ROXO}` }, gravando)}>Lançar</button>
                        <div><button onClick={() => setRascunhos(l => l.filter(x => x.id !== r.id))} style={{ ...botaoPequeno, marginTop: 4 }}>Remover</button></div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {prontos.length > 1 && (
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 10 }}>
              <button onClick={() => lancar(prontos)} disabled={gravando} style={desabilitado(botaoPrimario, gravando)}>
                {gravando ? 'Lançando…' : `Lançar ${prontos.length} notas (${fmtMoeda(prontos.reduce((s, r) => s + (parseFloat(r.valor) || 0), 0))})`}
              </button>
            </div>
          )}
        </>
      )}

      {lancadasNoMes.length > 0 && (
        <div style={{ marginTop: 12, fontSize: 12, color: SUAVE }}>
          <strong style={{ color: TEXTO }}>Lançadas em {nomeMes}:</strong>{' '}
          {lancadasNoMes.map(l => `${l.documento || 'NF'} ${fmtMoeda(l.valor)}`).join(' · ')}
        </div>
      )}
    </div>
  );
}
